import supabaseClient from '@/api/supabaseClient.ts';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { useQuery } from '@tanstack/react-query';

export type FrameworkPreview = {
  primary_framework: string | null;
  supporting_frameworks: string[];
  routing_confidence: number;
};

type FrameworkPreviewApiResponse = {
  primary_frameworks?: { name: string; confidence?: number }[];
  primary_framework?: string | null;
  supporting_frameworks?: { name: string }[] | string[];
  routing_confidence: number;
};

function normalizePreview(raw: FrameworkPreviewApiResponse): FrameworkPreview {
  const primary =
    raw.primary_framework ??
    raw.primary_frameworks?.[0]?.name ??
    null;

  const supporting = (raw.supporting_frameworks ?? []).map((item) =>
    typeof item === 'string' ? item : item.name,
  );

  return {
    primary_framework: primary,
    supporting_frameworks: supporting,
    routing_confidence: raw.routing_confidence ?? 0,
  };
}

/**
 * Live rule-based framework preview (no ledger / no credits).
 * @param sector — org industry / sector context (onboarding industry field)
 * @param orientation — org strategic_orientation
 * @param caseText — full intake case text; server matches keywords via substring
 */
const useFrameworkPreview = (
  sector: string,
  orientation: string,
  caseText = '',
  enabled = true,
) => {
  return useQuery({
    queryKey: ['framework-preview', sector, orientation, caseText],
    queryFn: () => fetchPreview(sector, orientation, caseText),
    enabled: enabled && (!!sector || !!orientation || !!caseText),
    staleTime: 1000 * 60 * 60,
  });
};

export default useFrameworkPreview;

async function fetchPreview(
  sector: string,
  orientation: string,
  caseText: string,
): Promise<FrameworkPreview> {
  const { data, error } = await supabaseClient.functions.invoke('framework-preview', {
    body: { sector, orientation, issue_signals: caseText },
  });

  if (error && error instanceof FunctionsHttpError) {
    const err = await error.context.json();
    throw new Error(err.errors?.join(', ') ?? 'Framework preview failed');
  }

  return normalizePreview(data.data as FrameworkPreviewApiResponse);
}
